// ---------------------------------------------------------------------------
// Beziehungen prüfen (Roadmap AP-44, PF 27): was die Beziehungen (relations.ts) gegen Lebensdaten (lifespan.ts) und Auftritte sagen.
//  - Eltern und Kind: das Elternteil wird nach dem Kind geboren; es ist bei der Geburt sehr jung (unter zwölf, eine Frage); es ist bei der
//    Geburt schon tot – die Mutter überhaupt, der Vater (bzw. ohne Geschlecht) mehr als zehn Monate.
//  - Eine Beziehung beginnt („ab“) zu einer Zeit, zu der einer der beiden schon tot bzw. noch nicht geboren ist (Familie ausgenommen: sie
//    hat kein „ab“).
//  - Verheiratet, aber nie gemeinsam in einer Szene, solange die Ehe gilt (beide treten auf, nur nie zusammen – eine Frage).
// Die Reihenfolge kommt wie bei den übrigen Zustandsprüfungen aus der Weltzeit (sicher) bzw. der Lesereihenfolge (wahrscheinlich).
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { afterPriorEvent, ageBetween, lifeOf, type Life } from './lifespan.js';
import { appearanceMatrix, narrativeOrder } from './narrative.js';
import { genderOf, relationActiveAt, relationEntries } from './relations.js';
import { stateEvents } from './states.js';
import { lifeConflict, sceneBefore, type Certainty } from './stateConflicts.js';
import type { PriorSource, Relation } from './types.js';

/** Unter diesem Alter (Jahre) ist ein Elternteil bei der Geburt des Kindes auffällig jung */
export const PARENT_MIN_AGE = 12;
/** Ein Vater kann so viele Monate vor der Geburt des Kindes gestorben sein */
const FATHER_MONTHS = 10;

export type RelationFindingKind = 'parent-younger' | 'parent-young' | 'parent-dead' | 'relation-life' | 'married-apart';

export interface RelationFinding {
  kind: RelationFindingKind;
  relation: Relation;
  /** Element, an dem die Beziehung steht (bei Eltern und Kind: das Elternteil) */
  sourceId: string;
  /** um wen es geht: das Elternteil bzw. wer zum Beginn der Beziehung nicht lebt */
  entityId: string;
  /** Szene, an der es sich zeigt (Geburt, Beginn der Beziehung); null: an keiner (verheiratet, nie zusammen) */
  sceneId: string | null;
  certainty: Certainty;
  /** Szene der Geburt bzw. des Todes, auf die sich der Befund stützt (leer: in einer früheren Episode der Serie, dann `prior`) */
  eventSceneId?: string;
  prior?: PriorSource;
  /** `relation-life`: schon tot bzw. noch nicht geboren */
  state?: 'dead' | 'unborn';
  /** `parent-young`: Alter des Elternteils bei der Geburt in Jahren */
  years?: number;
}

/** Befunde je Stand der Geschichte (ein neuer Stand ist ein neuer Kontext) */
const cache = new WeakMap<StoryCtx, RelationFinding[]>();

export function relationFindings(ctx: StoryCtx): RelationFinding[] {
  const hit = cache.get(ctx);
  if (hit) return hit;
  const out: RelationFinding[] = [];
  const all = stateEvents(ctx);
  const lives = new Map<string, Life>();
  const life = (id: string): Life => {
    let l = lives.get(id);
    if (!l) lives.set(id, (l = lifeOf(ctx, id, all)));
    return l;
  };
  let reading: Map<string, number> | null = null;
  const readingOf = (): Map<string, number> => (reading ??= new Map(narrativeOrder(ctx).scenes.map((s, i) => [s.scene.id, i])));
  let matrix: ReturnType<typeof appearanceMatrix> | null = null;

  for (const { sourceId, relation: r } of relationEntries(ctx)) {
    if (!ctx.entities.has(r.targetId)) continue;
    if (r.kind === 'elternteil') {
      const parent = life(sourceId);
      const child = life(r.targetId);
      // geboren in einer früheren Episode: dort schon geprüft
      if (!child.born || child.born.prior) continue;
      const childBirth = ctx.nodes.get(child.born.sceneId)!;
      const base = { relation: r, sourceId, entityId: sourceId };
      if (parent.born && !parent.born.prior) {
        const parentBirth = ctx.nodes.get(parent.born.sceneId)!;
        const younger = sceneBefore(ctx, childBirth, parentBirth, readingOf());
        if (younger) out.push({ ...base, kind: 'parent-younger', sceneId: parentBirth.id, certainty: younger, eventSceneId: childBirth.id });
        else if (parent.born.span && child.born.span) {
          const age = ageBetween(parent.born.span, child.born.span, parent.born.approx || child.born.approx);
          if (age && age.years < PARENT_MIN_AGE) out.push({ ...base, kind: 'parent-young', sceneId: childBirth.id, certainty: 'likely', eventSceneId: parentBirth.id, years: age.years });
        }
      }
      if (parent.died) {
        const death = parent.died.prior ? null : ctx.nodes.get(parent.died.sceneId)!;
        // gestorben in einer früheren Episode: vor der Geburt, außer die Zeiten sagen anderes
        const before = death ? sceneBefore(ctx, death, childBirth, readingOf()) : afterPriorEvent(ctx, childBirth, parent.died);
        if (before && genderOf(ctx, sourceId) === 'w') out.push({ ...base, kind: 'parent-dead', sceneId: childBirth.id, certainty: before, eventSceneId: death?.id ?? '', ...(parent.died.prior ? { prior: parent.died.prior } : {}) });
        else if (before === 'sure' && parent.died.span && child.born.span) {
          const gap = ageBetween(parent.died.span, child.born.span, parent.died.approx || child.born.approx);
          if (gap && gap.months > FATHER_MONTHS) out.push({ ...base, kind: 'parent-dead', sceneId: childBirth.id, certainty: 'sure', eventSceneId: death?.id ?? '', ...(parent.died.prior ? { prior: parent.died.prior } : {}) });
        }
      }
      continue;
    }
    if (r.kind === 'geschwister') continue;
    // Beginn der Beziehung: leben beide?
    const start = r.from ? ctx.nodes.get(r.from) : undefined;
    if (start)
      for (const id of [sourceId, r.targetId]) {
        const c = lifeConflict(ctx, start, id);
        if (c) out.push({ kind: 'relation-life', relation: r, sourceId, entityId: id, sceneId: start.id, certainty: c.certainty, eventSceneId: c.eventSceneId, state: c.state, ...(c.prior ? { prior: c.prior } : {}) });
      }
    // verheiratet, aber nie zusammen in einer Szene (solange die Ehe gilt)
    if (r.kind === 'ehe') {
      matrix ??= appearanceMatrix(ctx, 'world');
      const world = new Map(matrix.scenes.map((s, i) => [s.id, i]));
      const rowOf = (id: string) => matrix!.rows.find((row) => row.entityId === id);
      const a = rowOf(sourceId);
      const b = rowOf(r.targetId);
      const stage = (cell: readonly string[] | undefined): boolean => !!cell?.some((l) => l !== 'mentioned');
      let seenA = false;
      let seenB = false;
      let together = false;
      matrix.scenes.forEach((_, i) => {
        if (!relationActiveAt(r, i, world)) return;
        const inA = stage(a?.cells[i]);
        const inB = stage(b?.cells[i]);
        seenA ||= inA;
        seenB ||= inB;
        together ||= inA && inB;
      });
      if (seenA && seenB && !together) out.push({ kind: 'married-apart', relation: r, sourceId, entityId: sourceId, sceneId: null, certainty: 'likely' });
    }
  }
  cache.set(ctx, out);
  return out;
}
